import React, { useState, useEffect } from 'react';

interface StageRecord {
  durationMs?: number;
  completedAt?: string;
  error?: string;
  status?: string;
  outputHash?: string;
}

interface PipelineViewProps {
  slug: string;
  stages: Record<string, StageRecord>;
  runningStage?: string;
  completedStages?: string[];
  failedStage?: string;
}

// Full pipeline in order — always show all stages so user sees the journey
const PIPELINE_STAGES = [
  { key: 'ingest', name: 'Import source', desc: 'Loading your content into the engine' },
  { key: 'research', name: 'Research', desc: 'Extracting claims, facts, and references' },
  { key: 'plan', name: 'Plan structure', desc: 'Designing sections and word targets' },
  { key: 'compose', name: 'Write narrative', desc: 'Generating the full article from research' },
  { key: 'render', name: 'Render formats', desc: 'Creating Blog, Social, Reddit, and more' },
  { key: 'validate', name: 'Quality check', desc: 'Scoring readability, accuracy, and structure' },
];

function formatMs(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function humanTitle(slug: string): string {
  return slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

/** Live elapsed timer for the active stage */
function ElapsedTimer() {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const start = Date.now();
    const id = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(id);
  }, []);
  return <span className="pipeline-step__elapsed">{elapsed}s</span>;
}

export default function PipelineView({ slug, stages, runningStage, completedStages = [], failedStage }: PipelineViewProps) {
  // Count completed — check both SSE completedStages and stage records (outputHash)
  const doneCount = PIPELINE_STAGES.filter(s => {
    if (completedStages.includes(s.key)) return true;
    const rec = stages[s.key];
    return rec && (rec.outputHash || rec.status === 'completed');
  }).length;
  const totalCount = PIPELINE_STAGES.length;
  const progressPct = Math.round((doneCount / totalCount) * 100);
  // #285 QW4: title reflects real state (it always said "Queued", contradicting
  // the "running autonomously" subtitle).
  const phaseLabel = failedStage ? 'Failed'
    : doneCount === totalCount ? 'Complete'
    : (runningStage || doneCount > 0) ? 'Running'
    : 'Queued';

  return (
    <div className="pipeline-view">
      {/* Header with context */}
      <div className="pipeline-view__header">
        <div className="pipeline-view__title">
          <span className={'pipeline-view__spinner' + (phaseLabel === 'Complete' || phaseLabel === 'Failed' ? ' pipeline-view__spinner--static' : '')}>{phaseLabel === 'Complete' ? '\u2713' : phaseLabel === 'Failed' ? '\u2717' : '\u25CB'}</span>
          {phaseLabel}: {humanTitle(slug)}
        </div>
        <div className="pipeline-view__subtitle">
          {phaseLabel === 'Queued'
            ? <>Queued — the engine will research, write, then render this into every configured format. No action needed.</>
            : phaseLabel === 'Failed'
              ? <>A stage failed. See the highlighted step below for the reason.</>
              : phaseLabel === 'Complete'
                ? <>All stages complete. Review your renditions below.</>
                : <>Researching, writing, and rendering in the background. No action needed.</>}
        </div>
      </div>

      {/* Progress bar */}
      <div className="pipeline-view__progress-track">
        <div className="pipeline-view__progress-fill" style={{ transform: `scaleX(${progressPct / 100})` }} />
      </div>
      <div className="pipeline-view__progress-label">
        {doneCount} of {totalCount} stages complete
      </div>

      {/* Stage list — always show all 6 */}
      <div className="pipeline-view__steps">
        {PIPELINE_STAGES.map((stage, i) => {
          const rec = stages[stage.key];
          const isDone = completedStages.includes(stage.key) || (rec && (rec.outputHash || rec.status === 'completed'));
          const isActive = runningStage === stage.key;
          const isFailed = failedStage === stage.key;
          const isLast = i === PIPELINE_STAGES.length - 1;

          // Determine visual state
          let statusClass = '--pending';
          let icon = '\u25CB'; // ○
          if (isFailed) { statusClass = '--failed'; icon = '\u2717'; }
          else if (isDone) { statusClass = '--done'; icon = '\u2713'; }
          else if (isActive) { statusClass = '--active'; icon = '\u25B6'; }

          return (
            <div key={stage.key} className={`pipeline-step pipeline-step${statusClass}`}>
              <div className="pipeline-step__left">
                <div className={`pipeline-step__indicator pipeline-step__indicator${statusClass}`}>
                  {icon}
                </div>
                {!isLast && <div className={`pipeline-step__line ${isDone ? 'pipeline-step__line--done' : ''}`} />}
              </div>
              <div className="pipeline-step__body">
                <div className="pipeline-step__name">
                  {stage.name}
                  {isDone && rec?.durationMs != null && (
                    <span className="pipeline-step__duration">{formatMs(rec.durationMs)}</span>
                  )}
                  {isActive && <ElapsedTimer />}
                </div>
                <div className={`pipeline-step__desc ${isActive ? 'pipeline-step__desc--active' : isDone ? 'pipeline-step__desc--done' : ''}`}>
                  {isFailed && rec?.error ? rec.error : stage.desc}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
